import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Alert, CircularProgress, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Undo, ArrowRightAlt } from '@mui/icons-material';
import {
  P, cardSx, labelSx, priBtn, outBtn, thSx, thTitle, PX, WorkspaceHeader, StatusPill,
} from '../../components/common/workspaceKit';
import { formatIndianNumber } from '../../utils/currency';
import { linkNav } from '../../utils/linkNav';
import {
  materialConversionService, Conversion, CONVERSION_TYPE_LABELS,
} from '../../services/materialConversionService';

/**
 * Material conversion detail.
 *
 * Shows the transaction as what it is: stock consumed on one side, stock
 * received on the other, a loss expensed in between, and a journal entry
 * linking the whole thing to the ledger.
 */

const fmtDate = (v?: string | null) =>
  (v ? new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—');

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ ...labelSx, mb: '4px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: P.strong }}>{value ?? '—'}</Typography>
  </Box>
);

const MaterialConversionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [conversion, setConversion] = useState<Conversion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    materialConversionService.get(id)
      .then(({ conversion: c }) => setConversion(c))
      .catch((e: any) => setError(e.message || 'Failed to load the conversion'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  const doReverse = async () => {
    if (!id) return;
    setBusy(true); setError(null);
    try {
      const { conversion: rev } = await materialConversionService.reverse(id, reverseReason);
      setReverseOpen(false);
      navigate(`/inventory/conversions/${rev.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to reverse the conversion');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  if (!conversion) return <Box sx={{ p: 4 }}><Alert severity="error">{error || 'Conversion not found'}</Alert></Box>;

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6 }}>
      <WorkspaceHeader
        title={conversion.conversion_number}
        subtitle={`${CONVERSION_TYPE_LABELS[conversion.conversion_type]} · ${fmtDate(conversion.conversion_date)}`}
        chip={<StatusPill label={conversion.status}
          tone={conversion.status === 'POSTED' ? 'green' : conversion.status === 'CANCELLED' ? 'red' : 'amber'} />}
        onBack={() => navigate('/inventory/conversions')}
        actions={
          conversion.status === 'POSTED' ? (
            <Button variant="outlined" startIcon={<Undo />} sx={outBtn} onClick={() => setReverseOpen(true)}>
              Reverse
            </Button>
          ) : null
        }
      />

      <Box sx={{ px: PX, pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Paper elevation={0} sx={{ ...cardSx, mb: 2, p: 2.5 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            <Field label="Type" value={CONVERSION_TYPE_LABELS[conversion.conversion_type]} />
            <Field label="Reason" value={conversion.reason || conversion.reason_code} />
            <Field label="Rule applied" value={conversion.rule_name || '—'} />
            <Field label="Value treatment" value={
              conversion.gl_treatment === 'REVALUE' ? 'Re-struck at standard cost' : 'Cost carried across'
            } />
            <Field label="From warehouse" value={conversion.source_warehouse_name} />
            <Field label="Into warehouse" value={conversion.dest_warehouse_name} />
            <Field label="Posted by" value={conversion.created_by_name} />
            <Field label="Journal entry" value={
              conversion.journal_entry_number ? (
                <Box component="span" sx={{ color: P.primary, cursor: 'pointer', fontFamily: 'monospace' }}
                  {...linkNav(`/finance/journal-entries/${conversion.journal_entry_id}`, navigate)}>
                  {conversion.journal_entry_number}
                </Box>
              ) : 'none — same account, no value change'
            } />
          </Box>
          {conversion.remarks && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${P.line}` }}>
              <Typography sx={{ ...labelSx, mb: '4px' }}>Remarks</Typography>
              <Typography sx={{ fontSize: '13px', color: P.text, whiteSpace: 'pre-wrap' }}>{conversion.remarks}</Typography>
            </Box>
          )}
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
          {[
            { label: 'Value consumed', value: Number(conversion.total_source_value), tone: P.strong },
            { label: 'Loss written off', value: Number(conversion.loss_value), tone: Number(conversion.loss_value) > 0 ? P.red : P.muted },
            { label: 'Value received', value: Number(conversion.total_dest_value), tone: P.primary },
          ].map((m) => (
            <Paper key={m.label} elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Typography sx={{ ...labelSx, mb: '6px' }}>{m.label}</Typography>
              <Typography sx={{ fontSize: '21px', fontWeight: 800, color: m.tone, lineHeight: 1.1 }}>
                {formatIndianNumber(m.value)}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>Lines</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}><Typography sx={thTitle}>From</Typography></TableCell>
                  <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Qty</Typography></TableCell>
                  <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Cost</Typography></TableCell>
                  <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Loss</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 40 }} />
                  <TableCell sx={thSx}><Typography sx={thTitle}>Into</Typography></TableCell>
                  <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Qty</Typography></TableCell>
                  <TableCell align="right" sx={thSx}><Typography sx={thTitle}>New cost</Typography></TableCell>
                  <TableCell sx={thSx}><Typography sx={thTitle}>Rule</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conversion.items.map((i) => (
                  <TableRow key={i.id} hover>
                    <TableCell>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: P.strong }}>{i.source_product_name}</Typography>
                      <Typography sx={{ fontSize: '11.5px', color: P.muted, fontFamily: 'monospace' }}>
                        {i.source_product_code}{i.source_batch_number ? ` · ${i.source_batch_number}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatIndianNumber(Number(i.source_quantity))}
                      <Typography component="span" sx={{ fontSize: '11px', color: P.muted, ml: '3px' }}>{i.source_uom}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
                      {formatIndianNumber(Number(i.source_value))}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: '13px', fontVariantNumeric: 'tabular-nums',
                      color: Number(i.loss_quantity) > 0 ? P.red : P.muted,
                    }}>
                      {Number(i.loss_quantity) > 0
                        ? `${formatIndianNumber(Number(i.loss_quantity))} (${formatIndianNumber(Number(i.loss_value))})`
                        : '—'}
                    </TableCell>
                    <TableCell align="center"><ArrowRightAlt sx={{ fontSize: 20, color: P.primary }} /></TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: P.strong }}>{i.dest_product_name}</Typography>
                      <Typography sx={{ fontSize: '11.5px', color: P.muted, fontFamily: 'monospace' }}>
                        {i.dest_product_code}{i.dest_batch_number ? ` · ${i.dest_batch_number}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: P.green, fontVariantNumeric: 'tabular-nums' }}>
                      {formatIndianNumber(Number(i.dest_quantity))}
                      <Typography component="span" sx={{ fontSize: '11px', color: P.muted, ml: '3px' }}>{i.dest_uom}</Typography>
                      {Number(i.conversion_factor) !== 1 && (
                        <Typography sx={{ fontSize: '10.5px', color: P.muted }}>× {Number(i.conversion_factor)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatIndianNumber(Number(i.dest_unit_cost))}
                    </TableCell>
                    <TableCell>
                      {i.rule_code && (
                        <Chip size="small" label={i.rule_code}
                          sx={{ height: 20, fontSize: '11px', fontWeight: 700, bgcolor: P.primarySoft, color: P.primary }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={reverseOpen} onClose={() => setReverseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '17px', fontWeight: 800, color: P.strong }}>
          Reverse {conversion.conversion_number}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13.5px', color: P.text, mb: 2 }}>
            A counter-movement will be posted. The original document, its cost layers and its journal entry
            stay exactly as they are — they are the record of what actually happened.
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: P.muted, mb: 2 }}>
            This only works while the converted goods are still on hand. If any of them have been sold,
            issued or converted again, record a stock adjustment instead.
          </Typography>
          <TextField
            fullWidth size="small" label="Reason for reversal" value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)} autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReverseOpen(false)} sx={outBtn} disabled={busy}>Cancel</Button>
          <Button onClick={doReverse} sx={priBtn} disabled={busy || !reverseReason.trim()}
            startIcon={busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Undo />}>
            Post Reversal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialConversionDetailPage;
